import { useReducer, useRef, useEffect, useCallback } from "react";
import uuidv4 from "uuid4";
import ChatService from "../../../../front-services/ChatService";
import { useSliderState } from "../../components/slider/SliderState";
import { createAudioCapture } from "../audio/capture";
import { createAudioPlayback } from "../audio/playback";
import type {
  ServerEvent,
  TurnState,
  LatencyStats,
  ActivityItem,
  VoiceState,
} from "../types";

// ---------------------------------------------------------------------------
// Initial state
// ---------------------------------------------------------------------------

const initialTurn: TurnState = {
  active: false,
  turnStartTs: null,
  sttStartTs: null,
  sttEndTs: null,
  agentStartTs: null,
  agentEndTs: null,
  ttsStartTs: null,
  ttsEndTs: null,
  transcript: "",
  response: "",
  metadata: null,
};

const initialLatency: LatencyStats = {
  turns: 0,
  stt: [],
  agent: [],
  tts: [],
  total: [],
};

const initialState: VoiceState = {
  status: "idle",
  currentTurn: initialTurn,
  waterfallData: null,
  latencyStats: initialLatency,
  activities: [],
};

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

type Action =
  | { type: "SET_STATUS"; payload: VoiceState["status"] }
  | { type: "STT_CHUNK"; ts: number; transcript: string }
  | { type: "STT_OUTPUT"; ts: number; transcript: string }
  | { type: "AGENT_CHUNK"; ts: number; text: string }
  | { type: "AGENT_COMPLETE"; metadata: any[] | null }
  | { type: "TOOL_CALL"; name: string; args: Record<string, unknown> }
  | { type: "TOOL_RESULT"; name: string; result: string }
  | { type: "TTS_CHUNK"; ts: number }
  | { type: "FINISH_TURN" }
  | { type: "CLEAR_ACTIVITIES" }
  | { type: "RESET" };

function reducer(state: VoiceState, action: Action): VoiceState {
  switch (action.type) {
    case "SET_STATUS":
      return { ...state, status: action.payload };

    case "STT_CHUNK": {
      const turn = state.currentTurn;
      const isNewTurn = !turn.active;

      const waterfallData =
        isNewTurn && turn.turnStartTs ? { ...turn } : state.waterfallData;

      const newTurn: TurnState = isNewTurn
        ? {
            ...initialTurn,
            active: true,
            turnStartTs: action.ts,
            sttStartTs: action.ts,
            transcript: action.transcript,
          }
        : {
            ...turn,
            sttStartTs: turn.sttStartTs ?? action.ts,
            transcript: action.transcript,
          };

      return { ...state, currentTurn: newTurn, waterfallData };
    }

    case "STT_OUTPUT": {
      const activity: ActivityItem = {
        id: uuidv4(),
        type: "stt",
        label: "Transcription",
        text: action.transcript,
        timestamp: new Date(),
      };
      return {
        ...state,
        currentTurn: {
          ...state.currentTurn,
          sttEndTs: action.ts,
          transcript: action.transcript,
        },
        activities: [activity, ...state.activities].slice(0, 50),
      };
    }

    case "AGENT_CHUNK": {
      const t = state.currentTurn;
      return {
        ...state,
        currentTurn: {
          ...t,
          agentStartTs: t.agentStartTs ?? action.ts,
          agentEndTs: action.ts,
          response: t.response + action.text,
        },
      };
    }

    case "AGENT_COMPLETE":
      return {
        ...state,
        currentTurn: { ...state.currentTurn, metadata: action.metadata },
      };

    case "TOOL_CALL": {
      const activity: ActivityItem = {
        id: uuidv4(),
        type: "tool",
        label: `Tool: ${action.name}`,
        text: "Called with arguments:",
        args: action.args,
        timestamp: new Date(),
      };
      return {
        ...state,
        activities: [activity, ...state.activities].slice(0, 50),
      };
    }

    case "TOOL_RESULT": {
      const activity: ActivityItem = {
        id: uuidv4(),
        type: "tool",
        label: `Tool Result: ${action.name}`,
        text: action.result,
        timestamp: new Date(),
      };
      return {
        ...state,
        activities: [activity, ...state.activities].slice(0, 50),
      };
    }

    case "TTS_CHUNK": {
      const t = state.currentTurn;
      const isFirstChunk = !t.ttsStartTs && !!t.response;

      const newActivities = isFirstChunk
        ? [
            {
              id: uuidv4(),
              type: "agent" as const,
              label: "Agent Response",
              text: t.response,
              timestamp: new Date(),
            },
            ...state.activities,
          ].slice(0, 50)
        : state.activities;

      return {
        ...state,
        currentTurn: {
          ...t,
          ttsStartTs: t.ttsStartTs ?? action.ts,
          ttsEndTs: action.ts,
        },
        activities: newActivities,
      };
    }

    case "FINISH_TURN": {
      const t = state.currentTurn;
      const sttLatency =
        t.sttEndTs && t.sttStartTs ? t.sttEndTs - t.sttStartTs : null;
      const agentLatency =
        t.agentEndTs && t.agentStartTs ? t.agentEndTs - t.agentStartTs : null;
      const ttsLatency =
        t.ttsEndTs && t.ttsStartTs ? t.ttsEndTs - t.ttsStartTs : null;

      let newLatency = state.latencyStats;
      if (
        sttLatency !== null &&
        agentLatency !== null &&
        ttsLatency !== null
      ) {
        newLatency = {
          turns: state.latencyStats.turns + 1,
          stt: [...state.latencyStats.stt, sttLatency],
          agent: [...state.latencyStats.agent, agentLatency],
          tts: [...state.latencyStats.tts, ttsLatency],
          total: [
            ...state.latencyStats.total,
            sttLatency + agentLatency + ttsLatency,
          ],
        };
      }

      return {
        ...state,
        currentTurn: { ...t, active: false },
        waterfallData: { ...t },
        latencyStats: newLatency,
      };
    }

    case "CLEAR_ACTIVITIES":
      return { ...state, activities: [] };

    case "RESET":
      return initialState;

    default:
      return state;
  }
}

// ---------------------------------------------------------------------------
// URL helper
// ---------------------------------------------------------------------------

function getVoiceWsUrl(
  assistantId: string,
  threadId: string | null
): string {
  const isProd = process.env.REACT_APP_IS_PROD === "yes";
  const isHM = ChatService.isHM(assistantId);

  let httpBase: string;
  if (isHM) {
    httpBase = isProd
      ? (process.env.REACT_APP_DENGINE_API_HM_PROD ?? "")
      : (process.env.REACT_APP_DENGINE_API_HM_DEV ?? "");
  } else {
    httpBase = isProd
      ? (process.env.REACT_APP_DENGINE_API_PROD ?? "")
      : (process.env.REACT_APP_DENGINE_API_DEV ?? "");
  }

  const wsBase = httpBase
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://")
    .replace(/\/$/, "");

  const params = new URLSearchParams({ drastik_assistant_id: assistantId });
  if (threadId) params.set("thread_id", threadId);
  return `${wsBase}/voice/ws?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useVoiceSession(
  assistantId: string,
  threadId: string | null,
  isMobile: boolean = false,
  onThreadIdChange?: (threadId: string) => void,
  onClose?: () => void
) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const wsRef = useRef<WebSocket | null>(null);
  const audioCaptureRef = useRef(createAudioCapture());
  const audioPlaybackRef = useRef(createAudioPlayback());
  const suppressTTSRef = useRef(false);
  const ttsFinishTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Deferred audio-start: set in start(), called only when session.ready arrives
  const startAudioRef = useRef<(() => Promise<void>) | null>(null);

  // Keep latest state accessible in event handlers without stale closures
  const stateRef = useRef(state);
  stateRef.current = state;

  // Keep latest callback accessible without stale closures
  const onThreadIdChangeRef = useRef(onThreadIdChange);
  onThreadIdChangeRef.current = onThreadIdChange;

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const finishTurn = useCallback(() => {
    dispatch({ type: "FINISH_TURN" });
  }, []);

  const handleEvent = useCallback(
    (event: ServerEvent) => {
      const playback = audioPlaybackRef.current;

      switch (event.type) {
        case "session.ready":
          // Server confirmed the thread_id and is ready to receive audio
          onThreadIdChangeRef.current?.(event.thread_id);
          // Start microphone only now — server STT is initialised and listening
          startAudioRef.current?.();
          dispatch({ type: "SET_STATUS", payload: "listening" });
          break;

        case "stt_chunk":
          // Barge-in: user is speaking while agent audio is playing
          if (playback.isPlaying()) {
            playback.stop();
            suppressTTSRef.current = true;
          }
          dispatch({ type: "STT_CHUNK", ts: event.ts, transcript: event.transcript });
          break;

        case "stt_output":
          suppressTTSRef.current = false;
          dispatch({ type: "STT_OUTPUT", ts: event.ts, transcript: event.transcript });
          break;

        case "message.delta":
          suppressTTSRef.current = false;
          dispatch({ type: "AGENT_CHUNK", ts: Date.now(), text: event.delta });
          break;

        case "tool.started":
          dispatch({ type: "TOOL_CALL", name: event.title ?? event.tool, args: {} });
          break;

        case "message.completed": {
          // Store metadata (images, handoff button, etc.) on the current turn
          dispatch({
            type: "AGENT_COMPLETE",
            metadata: event.metadata ?? null,
          });

          // v2 templates arrive as structured objects — no JSON.parse.
          const templates = event.templates;
          if (Array.isArray(templates) && templates.length > 0) {
            useSliderState.getState().openSlider(
              threadId ?? "",
              assistantId,
              templates,
              isMobile
            );
          }
          break;
        }

        case "tts_chunk":
          dispatch({ type: "TTS_CHUNK", ts: event.ts });
          if (!suppressTTSRef.current) {
            playback.push(event.audio);
          }
          // Debounce: finish turn 300ms after last TTS chunk
          if (ttsFinishTimeoutRef.current) {
            clearTimeout(ttsFinishTimeoutRef.current);
          }
          ttsFinishTimeoutRef.current = setTimeout(() => {
            const t = stateRef.current.currentTurn;
            if (t.active && t.sttEndTs && t.ttsEndTs) {
              finishTurn();
            }
          }, 300);
          break;

        case "hangup":
          // Server requests call termination. Drain any remaining audio
          // (farewell TTS) before tearing down so that the caller hears
          // the complete ending message. The drain works by deferring
          // stop() until the audio sources have finished naturally.
          if (ttsFinishTimeoutRef.current) {
            clearTimeout(ttsFinishTimeoutRef.current);
          }
          audioPlaybackRef.current.drainThenStop().then(() => {
            audioCaptureRef.current.stop();
            if (wsRef.current) {
              wsRef.current.close();
              wsRef.current = null;
            }
          });
          break;
      }
    },
    [finishTurn, assistantId, threadId, isMobile]
  );

  const stop = useCallback(() => {
    if (ttsFinishTimeoutRef.current) {
      clearTimeout(ttsFinishTimeoutRef.current);
      ttsFinishTimeoutRef.current = null;
    }

    startAudioRef.current = null;
    audioPlaybackRef.current.stop();
    audioCaptureRef.current.stop();

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    dispatch({ type: "RESET" });
  }, []);

  const start = useCallback(async () => {
    dispatch({ type: "RESET" });
    suppressTTSRef.current = false;
    audioPlaybackRef.current.stop();
    dispatch({ type: "SET_STATUS", payload: "connecting" });

    const url = getVoiceWsUrl(assistantId, threadId);
    const ws = new WebSocket(url);
    wsRef.current = ws;

    // Register the deferred audio-start — called only when session.ready arrives,
    // so the server STT is guaranteed to be initialised before we send audio.
    startAudioRef.current = async () => {
      startAudioRef.current = null; // run once
      try {
        await audioCaptureRef.current.start((chunk) => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(chunk);
          }
        });
      } catch (err) {
        console.error("Microphone error:", err);
        dispatch({ type: "SET_STATUS", payload: "error" });
        stop();
      }
    };

    ws.onopen = () => {
      // WebSocket connected — keep status as "connecting" until session.ready
    };

    ws.onmessage = (event) => {
      try {
        const serverEvent: ServerEvent = JSON.parse(event.data);
        handleEvent(serverEvent);
      } catch (err) {
        console.error("Failed to parse voice event:", err);
      }
    };

    ws.onclose = () => {
      const prevStatus = stateRef.current.status;
      dispatch({ type: "SET_STATUS", payload: "idle" });
      // Fallback: if the server closed the connection (e.g. after hangup event
      // or network drop) while the panel was still active, force-close the UI.
      if (prevStatus !== "idle" && prevStatus !== "connecting") {
        onCloseRef.current?.();
      }
    };

    ws.onerror = () => {
      dispatch({ type: "SET_STATUS", payload: "error" });
    };
  }, [assistantId, threadId, handleEvent, stop]);

  const clearActivities = useCallback(() => {
    dispatch({ type: "CLEAR_ACTIVITIES" });
  }, []);

  const getAnalyser = useCallback(
    () => audioCaptureRef.current.getAnalyser(),
    []
  );

  // Cleanup on unmount
  useEffect(() => {
    const audioPlayback = audioPlaybackRef.current;
    const audioCapture = audioCaptureRef.current;
    return () => {
      if (ttsFinishTimeoutRef.current) {
        clearTimeout(ttsFinishTimeoutRef.current);
      }
      audioPlayback.stop();
      audioCapture.stop();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  return { state, start, stop, clearActivities, getAnalyser };
}
